import React, {
  createContext,
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { VariableSizeList as VariableWindowList } from 'react-window';
import styled from 'styled-components';
import { CollapseIcon } from 'components/atoms/collapse/collapse-icon';
import { Loading } from 'components/atoms/loading';
import { Option as OptionT, SelectMode } from 'components/atoms/select';
import { optionHeight } from 'components/atoms/select/constants';
import { GroupHeading } from './group-heading';

/**
 * Need context to access data and set handler
 * in `innerElementType` prop of react-window.
 */
const VirtualizationContext = createContext({} as any);

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  padding-left: var(--spacing-05);
  top: 50%;
  position: relative;
  transform: translateY(-50%);
  ${CollapseIcon} {
    flex-shrink: 0;
  }
  > div {
    width: calc(100% - var(--spacing-05));
  }
`;

const StickyHeaderWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;

  .sticky {
    position: sticky;
    top: 0;
    z-index: 999;
    background: #fff;
    height: 36px;
    color: var(--gray-50) !important;
    &:hover {
      background: var(--coolest-gray-10) !important;
    }
  }
`;

const ListContainer = styled.div`
  margin-bottom: var(--spacing-06);
`;

const StickyRow = ({ style, children }) => (
  <StickyHeaderWrapper style={style}>
    <div className="sticky">{children}</div>
  </StickyHeaderWrapper>
);

// List Virtualization
const computeMenuItemHeight = (
  option: OptionT & { type: 'groupHeading' | string },
  type: SelectMode,
) => {
  if (option.type === 'groupHeading') {
    return optionHeight.groupHeading;
  }

  if (type === 'user') {
    return option.description
      ? optionHeight.userDescription
      : optionHeight.user;
  }
  if (type === 'tag') {
    return option.description ? optionHeight.tagDescription : optionHeight.tag;
  }

  return optionHeight.basic;
};

const flattenGroupedChildrens = (children: ReactElement[]) => {
  return children
    .map((child) => {
      return child.props?.data?.__isNew__ === true
        ? [child]
        : [
            {
              props: {
                data: {
                  type: 'groupHeading',
                  label: child.props?.data?.label,
                },
              },
            },
            ...(child.props?.children ?? []),
          ];
    })
    .reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue),
      [],
    );
};

const RowRenderer = ({ index, style, data }) => {
  if (data[index]?.props?.data?.type === 'groupHeading') {
    return null;
  }

  if (data[index]?.props?.data?.type === 'loading') {
    return <Loading />;
  }

  return <div style={style}>{data[index]}</div>;
};

const getWrapperHeightForStickyHeader = (options): number => {
  return options.reduce((prev, _option) => {
    return prev + computeMenuItemHeight(_option.data, 'user');
  }, optionHeight.groupHeading);
};

const innerElementType = forwardRef(({ children, ...rest }) => {
  const getTop = useCallback(
    (index: number, stickyWrapperHeights: number[]): number => {
      if (index === 0) {
        return 0;
      }

      return stickyWrapperHeights
        .slice(0, index)
        .reduce((prev, current) => prev + current, 0);
    },
    [],
  );

  return (
    <VirtualizationContext.Consumer>
      {({
        groups,
        activeIndices,
        onHeadingClick,
        groupHeadingExtraProps,
        groupHeadingComponent,
      }) => {
        const stickyWrapperHeights: number[] = [];

        return (
          <ListContainer {...rest}>
            {groups
              .filter((group) => group.props?.data.__isNew__ !== true)
              .map((_group, index) => {
                if (!_group || !_group.props) {
                  return null;
                }
                const {
                  children: _children,
                  data,
                  ...headingPropsRest
                } = _group.props;
                const _options =
                  data?.id === 'assignees'
                    ? _children.map((_child) => _child.props)
                    : _children
                        .filter((_child) => !_child.props.isSelected)
                        .map((_child) => _child.props);

                const isActive = activeIndices.includes(index);
                const height = isActive
                  ? getWrapperHeightForStickyHeader(_options)
                  : optionHeight.groupHeading;

                stickyWrapperHeights.push(height);

                return (
                  <StickyRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={{
                      top: `${getTop(index, stickyWrapperHeights)}px`,
                      height,
                    }}
                  >
                    <AccordionHeader onClick={() => onHeadingClick(index)}>
                      <CollapseIcon className={isActive ? 'active' : ''} />
                      <GroupHeading
                        {...headingPropsRest}
                        data={data}
                        groupHeadingComponent={groupHeadingComponent}
                        {...groupHeadingExtraProps}
                      />
                    </AccordionHeader>
                  </StickyRow>
                );
              })
              .filter(Boolean)}

            {children}
          </ListContainer>
        );
      }}
    </VirtualizationContext.Consumer>
  );
});

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: {
      maxMenuHeight,
      grouped,
      type = 'basic',
      value,
      isMulti,
      components,
    },
    groupHeadingExtraProps,
    isLoadingPlaceholderResolutions,
    placeholderResolutions,
  } = props;

  const userIdsForPlaceholderResolution = useMemo(() => {
    return isMulti
      ? value
          ?.filter((_option) => _option?.extra?.type === 'user')
          ?.map((_option) => _option?.extra.id)
      : [];
  }, [isMulti, value]);

  const teamIdsForPlaceholderResolution = useMemo(() => {
    return isMulti
      ? value
          ?.filter((_option) => _option?.extra?.type === 'team')
          ?.map((_option) => _option?.extra.id)
      : [];
  }, [isMulti, value]);

  const [activeIndices, setActiveIndices] = useState([
    ...Array(children.length).keys(),
  ]);

  const onHeadingClick = useCallback(
    (_index) => {
      const isActive = activeIndices.includes(_index);

      setActiveIndices(
        isActive
          ? activeIndices.filter((i) => _index !== i)
          : [...activeIndices, _index],
      );
    },
    [activeIndices],
  );

  const filteredGroups = useMemo(() => {
    return children.map((child, index) => {
      return activeIndices.includes(index)
        ? child.props?.data?.id === 'assignees'
          ? child
          : child.props?.data?.id === 'users'
          ? isLoadingPlaceholderResolutions
            ? { ...child, props: { ...child.props, children: [] } }
            : {
                ...child,
                props: {
                  ...child.props,
                  children: child.props?.children.filter((_child) => {
                    return !(
                      _child?.props?.isSelected ||
                      teamIdsForPlaceholderResolution?.some((_teamId) =>
                        placeholderResolutions?.resolvedWithEntities?.[
                          `user_${_child.props.data.extra.id}`
                        ]?.includes(_teamId),
                      )
                    );
                  }),
                },
              }
          : child.props?.data?.id === 'teams'
          ? isLoadingPlaceholderResolutions
            ? { ...child, props: { ...child.props, children: [] } }
            : {
                ...child,
                props: {
                  ...child.props,
                  children: child.props?.children.filter((_child) => {
                    return !(
                      _child?.props?.isSelected ||
                      userIdsForPlaceholderResolution?.some((_userId) => {
                        const teamResolvedTo =
                          placeholderResolutions?.mappings?.[
                            _child.props.data.extra.id
                          ];

                        return (
                          teamResolvedTo?.identityType === 'USER' &&
                          teamResolvedTo?.userId === _userId
                        );
                      })
                    );
                  }),
                },
              }
          : {
              ...child,
              props: {
                ...child.props,
                children: Array.isArray(child.props?.children)
                  ? child.props?.children.filter(
                      (_child) => !_child?.props?.isSelected,
                    )
                  : child.props?.children,
              },
            }
        : { ...child, props: { ...child.props, children: [] } };
    });
  }, [
    activeIndices,
    children,
    isLoadingPlaceholderResolutions,
    placeholderResolutions,
    teamIdsForPlaceholderResolution,
    userIdsForPlaceholderResolution,
  ]);

  const transformedChild = useMemo(
    () => (grouped ? flattenGroupedChildrens(filteredGroups) : filteredGroups),
    [filteredGroups, grouped],
  );

  const listRef = React.useRef<VariableWindowList>(null);

  // Reset cache to make react window recalculate styles once the list of childrens are modified.
  const resetWindowedListCachePosition = () => {
    transformedChild.forEach((child, index) => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    });
  };

  useEffect(() => {
    resetWindowedListCachePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedChild.length]);

  return (
    <VirtualizationContext.Provider
      value={{
        groups: filteredGroups,
        activeIndices,
        onHeadingClick,
        groupHeadingExtraProps,
        groupHeadingComponent: components.GroupHeading,
      }}
    >
      <VariableWindowList
        height={maxMenuHeight}
        itemCount={transformedChild.length}
        innerElementType={innerElementType}
        itemData={transformedChild}
        ref={listRef}
        itemSize={(index: number) => {
          return computeMenuItemHeight(
            transformedChild[index]?.props?.data || [],
            type,
          );
        }}
        width="100%"
      >
        {RowRenderer}
      </VariableWindowList>
    </VirtualizationContext.Provider>
  );
};

export const VirtualizedMenuList = (props): JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};
